import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import { lazy } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAccountActivity } from '../../hooks/useAccount';
import { usePopularDocuments } from '../../hooks/useDocuments';
import { useAuthorStatsHighlights, useAttributionStatsHighlights } from '../../hooks/useStats';

import {
    useHasJoinedGroup,
    useIsAuthor,
    useIsInGroup,
    useUserAuthor,
    useUserRole,
} from '../../contexts/AuthContext';
import { usePromotions } from '../../contexts/PromotionsContext';
import { useSite, useSubscriptions } from '../../contexts/SiteContext';
import AvatarButton from '../buttons/AvatarButton';
import DocumentHorizontalCard from '../cards/DocumentHorizontalCard';
import PromotionVerticalCard from '../cards/PromotionVerticalCard';
import SubscriptionHorizontalCard from '../cards/SubscriptionHorizontalCard';
import * as NotificationComponents from '../cards/notifications';
import DocumentsList from '../lists/DocumentsList';
import GridList from '../lists/GridList';
import ProOnboarding from '../partials/ProOnboarding';
import RowTitle from '../partials/RowTitle';
import SectionTitle from '../typography/SectionTitle';

import styles from '../../styles/panels/account-panel.module.css';

const StatsHighlights = lazy(() => import('../partials/StatsHighlights'));

interface AccountPanelProps {
    newDaysCount?: number;
    className?: string;
    innerClassName?: string;
}

function AccountPanel({
    newDaysCount = 30,
    className = null,
    innerClassName = null,
}: AccountPanelProps) {
    const allSubscriptions = useSubscriptions();
    const user = useUser() as User | null;
    const { firstname = null } = user || {};
    const { id: authorId = null } = useUserAuthor() || {};
    const url = useUrlGenerator();
    const isInPro = useIsInGroup('pro');
    const isStaff = useUserRole() === 'staff';
    const hasJoinedPro = useHasJoinedGroup('pro');
    const isAuthor = authorId !== null;
    const isWaitingForPro = isInPro === true && hasJoinedPro === false;
    const { items: popularDocuments = null } = usePopularDocuments({
        count: 12,
        enabled: hasJoinedPro,
    });

    const { promotions = [] } = usePromotions();
    const filteredPromotions = (promotions || [])
        .filter(({ placement }) => placement === 'account')
        .slice(0, 6);

    const { items: activities, isLoading: activitiesLoading } = useAccountActivity({
        days: newDaysCount,
    });

    const subscriptions = allSubscriptions.filter(
        ({ type }) => type !== 'notification' && type !== 'external',
    );

    const authorsNotifications = allSubscriptions.filter(
        ({ type, author = null }) => type === 'notification' && author !== null,
    );

    const statsDays = 28;
    const { highlights: proHighlights, isLoading: isLoadingProHighlights } =
        useAttributionStatsHighlights(statsDays, {
            enabled: hasJoinedPro,
        });
    // const authorHighlights = useDocumentsStatsHighlights(authorId);
    const { highlights: authorHighlights, isLoading: isLoadingAuthorHighlights } =
        useAuthorStatsHighlights(authorId, statsDays, {
            enabled: isAuthor,
        });

    return (
        <div className={classNames([styles.container, className])}>
            {isWaitingForPro ? (
                <div className={classNames([styles.section, innerClassName])}>
                    <div className={styles.importantMessage}>
                        <p>
                            Votre adhésion aux <strong>collaborateurs URBANIA</strong> est en cours
                            de validation. Vous recevrez un courriel de confirmation sous peu.
                        </p>
                    </div>
                </div>
            ) : null}
            <section className={classNames([styles.section, innerClassName])}>
                <RowTitle
                    url={url('account.timeline')}
                    notBranded
                    className={styles.header}
                    titleClassName={styles.title}
                >
                    En résumé
                </RowTitle>
                <div className={styles.row}>
                    <div className={classNames([styles.box, styles.welcome])}>
                        <AvatarButton className={styles.avatar} />
                        <div className={styles.description}>
                            <h4>
                                <FormattedMessage
                                    defaultMessage="Bienvenu.e dans votre espace membre,"
                                    description="Account welcome message"
                                />
                                {`${firstname !== null ? ` ${firstname}` : ''} 👋`}
                            </h4>
                            <p>
                                <FormattedMessage
                                    defaultMessage="Retrouvez vos infolettres, les contenus qui ont capté votre attention, de quoi gérer vos abonnements, votre historique de navigation, et TOUUUS vos avantages en tant que membre URBANIA (car qui n’aime pas les perks?!). De rien."
                                    description="Account welcome message"
                                />
                            </p>
                        </div>
                    </div>
                    <div
                        className={classNames([
                            styles.box,
                            styles.notifications,
                            {
                                [styles.empty]: activities.length === 0 && !activitiesLoading,
                            },
                        ])}
                    >
                        {activities.map((activity) => {
                            const { id, type: itemType, item } = activity;
                            if (itemType === 'notification') {
                                const { type } = item;
                                const NotificationComponent = getComponentFromName(
                                    NotificationComponents,
                                    type,
                                );
                                return NotificationComponent !== null ? (
                                    <NotificationComponent
                                        key={`notification-${id}`}
                                        {...item}
                                        size="small"
                                        className={styles.item}
                                    />
                                ) : null;
                            }
                            if (itemType === 'newsletter') {
                                return (
                                    <DocumentHorizontalCard
                                        key={`newsletter-${id}`}
                                        {...item}
                                        ratio="square"
                                        size="small"
                                        withoutTime
                                        className={styles.item}
                                    />
                                );
                            }
                            return null;
                        })}
                        {activities.length === 0 && !activitiesLoading ? (
                            <div className={styles.noItems}>
                                <FormattedMessage
                                    defaultMessage="Aucune notification pour le moment."
                                    description="Account no new items"
                                />
                            </div>
                        ) : null}
                    </div>
                </div>
            </section>

            {hasJoinedPro || isStaff || isAuthor ? (
                <>
                    {!isStaff && hasJoinedPro ? (
                        <div className={classNames([styles.section, styles.proOnboarding])}>
                            <div className={classNames([styles.header, innerClassName])}>
                                <SectionTitle className={styles.title}>
                                    Comment collaborer
                                </SectionTitle>
                            </div>
                            <ProOnboarding itemsClassName={styles.items} />
                        </div>
                    ) : null}
                    {!isStaff && hasJoinedPro ? (
                        <div
                            className={classNames([
                                styles.section,
                                styles.proSuggestions,
                                innerClassName,
                            ])}
                        >
                            <RowTitle
                                url={url('popular')}
                                notBranded
                                className={styles.header}
                                titleClassName={styles.title}
                            >
                                Suggestions de partage
                            </RowTitle>
                            <DocumentsList
                                items={popularDocuments}
                                withoutLoader
                                presentation="grid"
                                cardType="share"
                                className={styles.list}
                            />
                        </div>
                    ) : null}
                    {isAuthor ? (
                        <div className={classNames([styles.section, innerClassName])}>
                            <RowTitle
                                url={url('account.stats')}
                                notBranded
                                className={styles.header}
                                titleClassName={styles.title}
                            >
                                Statistiques <small>28 jours</small>
                            </RowTitle>
                            <StatsHighlights
                                items={authorHighlights}
                                placeholder={isLoadingAuthorHighlights}
                            />
                        </div>
                    ) : null}
                    {!isStaff && hasJoinedPro ? (
                        <div className={classNames([styles.section, innerClassName])}>
                            <RowTitle
                                url={url('account.stats')}
                                notBranded
                                className={styles.header}
                                titleClassName={styles.title}
                            >
                                Statistiques de partage <small>28 jours</small>
                            </RowTitle>
                            <StatsHighlights
                                items={proHighlights}
                                placeholder={isLoadingProHighlights}
                            />
                        </div>
                    ) : null}
                </>
            ) : null}

            {filteredPromotions.length > 0 && !hasJoinedPro ? (
                <section className={classNames([styles.section, innerClassName])}>
                    <RowTitle
                        url={url('account.promotions')}
                        notBranded
                        className={styles.header}
                        titleClassName={styles.title}
                    >
                        Avantages
                    </RowTitle>
                    <GridList
                        items={filteredPromotions}
                        cardComponent={PromotionVerticalCard}
                        className={styles.list}
                        autoColumns
                        cardWidth={300}
                    />
                </section>
            ) : null}
            <section className={classNames([styles.section, innerClassName])}>
                <RowTitle
                    url={url('account.subscriptions')}
                    notBranded
                    className={styles.header}
                    titleClassName={styles.title}
                >
                    <FormattedMessage defaultMessage="Infolettres" description="Section title" />
                </RowTitle>
                <GridList<Subscription>
                    items={subscriptions}
                    cardComponent={SubscriptionHorizontalCard}
                    className={styles.list}
                    columns={1}
                    cardWith={['toggle']}
                    cardTheme="box"
                    cardRatio="square"
                    cardProps={{
                        source: 'account',
                    }}
                />
            </section>

            {authorsNotifications.length > 0 ? (
                <section className={classNames([styles.section, innerClassName])}>
                    <RowTitle
                        url={url('account.subscriptions')}
                        notBranded
                        className={styles.header}
                        titleClassName={styles.title}
                    >
                        Notifications
                    </RowTitle>

                    <GridList<Subscription>
                        items={authorsNotifications}
                        cardComponent={SubscriptionHorizontalCard}
                        className={styles.list}
                        cardWidth={300}
                        autoColumns
                        cardWith={['toggle']}
                        cardTheme="box"
                        cardRatio="square"
                        cardProps={{
                            source: 'account',
                        }}
                    />
                </section>
            ) : null}
        </div>
    );
}

export default AccountPanel;
